import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Alert, Badge, Modal, Pagination, Table } from '@evogard/ui';
import { count } from '@evogard/domain';
import type { ConsultantSummary, Period } from '@evogard/contract';
import { api } from '../../lib/api.js';
import { useSessionContext } from '../../lib/session.js';
import {
  CLASSIFICATION_LABEL,
  MoneyValue,
  WhyNotSettled,
  periodLabel,
} from './common.js';

/**
 * Dez linhas por página.
 *
 * Não é gosto: é a medida que fez o diálogo caber. Com as 37 placas de uma
 * competência, o modal media **1817px de altura numa janela de 800** — ele
 * começava 508px acima do topo da tela, e as primeiras linhas não eram
 * alcançáveis nem por rolagem, nem por teclado, nem por toque. A altura foi
 * consertada na peça (`Modal`, em `packages/ui`, que agora cabe na tela e rola
 * por dentro); dez linhas é o que evita que a resposta chegue como uma coluna
 * de trinta e sete para ser rolada.
 */
/**
 * As vendas por trás de uma posição do ranking — em modal, sem sair do quadro.
 *
 * ## A pergunta que ele responde, e por que a linha não bastava
 *
 * A linha do quadro diz "33" e, embaixo, "4 reativações fora". Isso responde
 * *quanto* ficou de fora e não responde **quais** — e "quais" é a pergunta que
 * a operação faz de verdade, porque é a que se confere contra a mesa de vendas,
 * onde as 37 linhas estão. Enquanto ela não tinha resposta, o número do ranking
 * era certo e inconferível, que é exatamente como os dois quadros antigos
 * conviviam (§5.6: 33 numa tela, 37 na outra, e nada explicando).
 *
 * ## A terceira exceção de `DESIGN.md`, e pelo mesmo motivo das duas primeiras
 *
 * "Modal como primeira ideia" está em "o que não fazemos". As duas exceções
 * abertas — encaminhar da mesa, visualizar a demanda — partem de uma LINHA de
 * lista, onde progressivo significaria navegar para outra tela e voltar. Aqui é
 * igual, e com um agravante: o ponto todo é **conferir** o número que está na
 * linha atrás do modal. Uma tela própria tiraria da vista justamente o número
 * que se quer conferir.
 *
 * ## `counted` vem do servidor, e a tela não recalcula nada
 *
 * A marca de cada linha é `venda.counted`, que o servidor produziu com
 * `countsForRanking` — a mesma função que produziu o total. Escrever aqui um
 * `classification !== 'reativacao'` daria o mesmo resultado hoje e seria a
 * terceira implementação da P5 nesta casa; as duas que existiam em produção já
 * discordavam entre si. Pela mesma razão o resumo **não soma** as linhas
 * marcadas: ele mostra `achieved` e `ignored` como vieram.
 *
 * ## O resumo é da COMPETÊNCIA, e a lista é da página
 *
 * Os dois números convivem de propósito e dizem coisas diferentes: "33 vendas
 * contadas · 4 reativações fora" é o mês inteiro — é o que se confere contra a
 * linha atrás do modal —, e "11 a 20 de 37" é o pedaço na tela. Um total que
 * encolhesse ao virar a página diria que a pessoa vendeu dez, e o modal
 * deixaria de servir para a única coisa que ele faz.
 *
 * ## Quatro colunas, e as duas linhas por célula não são enfeite
 *
 * Eram seis, e a tabela media 859px dentro de um modal de 672 — rolava de lado
 * antes de qualquer telefone existir. Agrupar veículo com associado e
 * classificação com situação é o mesmo recurso que a mesa de vendas usa na
 * célula de competência, e devolve a tabela ao tamanho do diálogo. O que ficou
 * de fora é o `idExterno`: ele identifica a venda na origem e não ajuda a
 * conferir contagem nenhuma, que é o que se veio fazer aqui.
 *
 * ## Ele recusa junto com o quadro
 *
 * Quando a competência não é apurável, o modal diz o motivo em vez de listar —
 * inclusive quando a placa sem categoria é de outro consultor. É a recusa da
 * competência inteira, a mesma do quadro; o alternativo seria a casa dizendo
 * "não sei contar março" atrás do modal e listando março dentro dele.
 *
 * ## Duas telas o abrem, e `source` é a ÚNICA diferença entre elas
 *
 * O ranking e o painel do time fazem a mesma pergunta — *quais placas desta
 * pessoa nesta competência, e quais contaram* —, e as duas rotas respondem o
 * mesmo schema, da mesma função pura, sobre a mesma leitura. O que muda é
 * **quem pode chamar**: lá o alcance é `sales.ranking:ver_vendas` ou o time de
 * quem olha; aqui é `sales.team_performance:ver_vendas` dentro do time que a
 * pessoa lidera.
 *
 * Por isso é um componente com um parâmetro, e não dois arquivos. Uma segunda
 * cópia desta tabela seria a segunda forma de explicar o mesmo número, e o dia
 * em que uma ganhasse uma coluna seria o dia em que as duas telas passariam a
 * explicar o mesmo total de jeitos diferentes.
 *
 * Ele recebe o `consultant`, e não a posição: a posição depende da ordem
 * escolhida no quadro, e o painel do time não tem posição nenhuma. O que as
 * duas telas têm na mão é a pessoa.
 */
export function RankingSalesModal({
  consultant,
  period,
  source,
  open,
  onClose,
}: {
  consultant: ConsultantSummary;
  period: Period;
  /** qual rota responde — ver o docblock. `ranking` é o quadro da praça. */
  source: 'ranking' | 'time';
  open: boolean;
  onClose: () => void;
}) {
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;
  const consultantId = consultant.id;
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  /*
   * A página volta para a primeira quando o modal passa a ser de outra pessoa.
   * Sem isto, fechar na página 4 e abrir a linha de quem tem 12 placas pediria
   * uma página que não existe — e a resposta certa do servidor (lista vazia)
   * chegaria como um modal em branco sobre uma posição que tem vendas.
   */
  useEffect(() => setPage(1), [consultantId, period, perPage]);

  const salesQuery = useQuery({
    /*
     * `source` entra na chave, e não é zelo: as duas rotas respondem o mesmo
     * schema com alcances diferentes, então a mesma pessoa pode voltar de uma
     * com 200 e da outra com 403. Fora da chave, o cache de uma responderia
     * pela outra e a tela mostraria uma lista que a rota dela recusaria.
     */
    queryKey: [
      'comercial',
      'vendas-do-consultor',
      source,
      tenantId,
      period,
      consultantId,
      page,
      perPage,
    ],
    enabled: open,
    queryFn: async () => {
      const r =
        source === 'ranking'
          ? await api.sales.rankingSales({ query: { period, consultantId, page, perPage } })
          : await api.sales.teamMemberSales({
              query: { period, consultantId, page, perPage },
            });
      if (r.status !== 200) {
        /*
         * O 404 é resposta prevista desta rota, e não uma falha: é o consultor
         * sem posição na competência. A tela não chega nele pelo botão — o
         * botão nasce numa linha que, por existir, é uma posição —, então ele
         * só aparece quando a competência mudou embaixo de um modal aberto.
         * "Não encontrado" ali seria verdade e não ajudaria ninguém.
         */
        throw new Error(
          r.status === 404
            ? 'Esta competência não tem mais uma posição para este consultor. Feche e carregue o quadro de novo.'
            : 'Não foi possível carregar as vendas desta posição.',
        );
      }
      return r.body;
    },
    /*
     * A página anterior fica na tela enquanto a nova não chega. Sem isto, cada
     * clique em "Próxima" troca a tabela por um esqueleto e o modal muda de
     * altura duas vezes por página — e o botão que a pessoa acabou de apertar
     * se move para debaixo do ponteiro.
     */
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[5] === consultantId ? previous : undefined,
  });

  const detail = salesQuery.data;

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={`Vendas de ${consultant.user.nome}`}
      descricao={`As placas da competência de ${periodLabel(period)} — as que contaram e as que não.`}
      size="lg"
    >
      {salesQuery.isPending && (
        <div className="flex flex-col gap-3">
          <span className="esqueleto block h-4 w-2/3" />
          <span className="esqueleto block h-20 w-full" />
        </div>
      )}

      {salesQuery.isError && <Alert tone="erro">{salesQuery.error.message}</Alert>}

      {detail?.estado === 'nao_apuravel' && (
        <WhyNotSettled
          title={`O ranking de ${periodLabel(period)} não é apurável.`}
          reasons={detail.reasons}
        >
          <p className="mt-1">
            Nenhuma venda é listada, e é a mesma recusa do quadro atrás deste modal: o
            motivo pode ser de outro consultor, porque a recusa é da competência inteira.
            Listar aqui o que o quadro se recusa a contar seria a mesma casa dizendo duas
            coisas sobre o mesmo mês.
          </p>
        </WhyNotSettled>
      )}

      {detail?.estado === 'apurado' && (
        <div className="flex flex-col gap-3">
          {/*
           * O total vem do servidor e repete o da linha que abriu o modal. Ele
           * está aqui para ser conferido contra a lista logo abaixo, que é o
           * motivo de este modal existir — nenhuma soma acontece nesta tela.
           */}
          <p className="text-sm text-texto-suave">
            <strong className="font-semibold text-texto">
              {count(detail.achieved, 'venda contada', 'vendas contadas')}
            </strong>
            {detail.ignored > 0 && (
              <> · {count(detail.ignored, 'reativação fora', 'reativações fora')}</>
            )}{' '}
            · <MoneyValue value={detail.value} /> no total contado
          </p>

          <Table
            framed
            paginate={false}
            label={`Vendas de ${consultant.user.nome} na competência`}
            items={detail.sales.items}
            rowKey={(v) => String(v.id)}
            empty="Nenhuma venda nesta competência."
            columns={[
              {
                title: 'Contou',
                minWidth: '6rem',
                cell: (v) =>
                  v.counted ? (
                    <Badge tone="sucesso">Conta</Badge>
                  ) : (
                    /*
                     * "Fora" e não "Não" — o que a linha diz é que a contagem
                     * da casa descarta aquela placa, e não que ela seja
                     * inválida. A venda existe, foi feita, e está na mesa.
                     */
                    <Badge tone="neutro">Fora</Badge>
                  ),
              },
              {
                /*
                 * Placa, modelo e nome do associado — e não mais o código cru.
                 *
                 * O comentário que estava aqui dizia "o CÓDIGO do veículo, não
                 * a placa: a placa é campo do espelho", e era verdade enquanto
                 * o espelho estava vazio. Desde 21/09/2026 o servidor resolve
                 * os dois códigos em lote e manda o rótulo junto da linha.
                 *
                 * Sem rótulo, mostra o código e **não escreve frase nenhuma**
                 * sobre o cadastro: ele pode faltar porque o código não existe
                 * lá ou porque o espelho não respondeu, e esta tela não tem
                 * como distinguir os dois.
                 */
                title: 'O que foi vendido',
                alignment: 'inicio' as const,
                minWidth: '13rem',
                cell: (v) => {
                  const model = [v.vehicle?.marca, v.vehicle?.modelo]
                    .filter(Boolean)
                    .join(' ');

                  return (
                    <div className="min-w-0">
                      {v.vehicle?.placa != null ? (
                        <p className="truncate font-medium tabular-nums">
                          {v.vehicle.placa}
                        </p>
                      ) : (
                        <p className="truncate tabular-nums text-texto-suave">
                          {v.codigoVeiculo ?? '—'}
                        </p>
                      )}

                      {model !== '' && (
                        <p className="mt-0.5 truncate text-xs text-texto-suave">
                          {model}
                          {v.vehicle?.anoModelo != null && ` ${v.vehicle.anoModelo}`}
                        </p>
                      )}

                      <p className="mt-0.5 truncate text-xs text-texto-suave">
                        {v.member?.nome ??
                          (v.codigoAssociado === null
                            ? 'sem associado'
                            : `associado ${v.codigoAssociado}`)}
                      </p>
                    </div>
                  );
                },
              },
              {
                title: 'Classificação',
                minWidth: '9rem',
                cell: (v) => (
                  <div className="min-w-0">
                    <p className="truncate">{CLASSIFICATION_LABEL[v.classification]}</p>
                    <p className="mt-0.5 truncate text-xs text-texto-suave">
                      {/* nulo aqui é "a origem não disse", e não "sem situação" */}
                      {v.situacao ?? 'a origem não disse'}
                    </p>
                  </div>
                ),
              },
              {
                title: 'Valor',
                alignment: 'fim',
                cell: (v) => <MoneyValue value={v.value} />,
              },
            ]}
          />

          <Pagination
            page={detail.sales.page}
            perPage={detail.sales.perPage}
            total={detail.sales.total}
            onChange={setPage}
            onPerPageChange={setPerPage}
            label="vendas desta posição"
          />
        </div>
      )}
    </Modal>
  );
}
